import React from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import {
  TextField,
  Button,
  MenuItem,
  Typography,
  Grid2 as Grid,
  Avatar,
  useTheme,
  DialogActions,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Divider,
} from '@mui/material'
import useCustomStyles from '../../hooks/CustomStylesHook'
import Colors from '../../utils/colors.json'
import CloseIcon from '@mui/icons-material/Close'
import { User } from './UserInfoCard'
import {
  USER_DIALOG_MODES,
  USER_GENDER,
  USER_STATUS,
} from '../../constants/constants'
import { UserRole } from './Users'

const styles = (theme: any) => ({
  formContainer: {},
  submitButton: {
    borderRadius: '20px',
  },
  avatar: {
    width: theme.spacing(12),
    height: theme.spacing(12),
    margin: 'auto',
    cursor: 'pointer',
  },
  formTextField: {
    fontSize: '14px',
    fontWeight: 400,
    color: Colors.TEXT_COLOR_SECONDARY,
    height: 40,
    borderRadius: '20px',
  },
  formSelectField: {
    fontSize: '14px',
    fontWeight: 400,
    color: Colors.TEXT_COLOR_SECONDARY,
    minWidth: '180px',
    height: 40,
    borderRadius: '20px',
  },
  fieldLabel: {
    fontSize: '14px',
    fontWeight: 400,
    color: Colors.TEXT_COLOR_QUATERNARY,
  },
  dialogActions: {
    justifyContent: 'center',
  },
  dialogCloseBtn: {
    position: 'absolute',
    right: 8,
    top: 8,
    color: theme.palette.grey[500],
  },
  userDialog: {
    backgroundColor: Colors.MODAL_BG,
    display: 'flex',
    flexDirection: 'column',
  },
})

const validationMessages = {
  requiredName: 'Name is required',
  requiredEmpId: 'User ID is required',
  requiredPhoneNumber: 'Phone number is required',
  invalidPhoneNumber: 'Phone number must be 10 digits',
  requiredEmailId: 'Email is required',
  invalidEmailId: 'Invalid email format',
  requiredRole: 'Role is required',
  requiredStatus: 'Status is required',
  requiredProfileImage: 'Profile image is required',
  requriedGender: 'Gender is requried',
  requriedUserName: 'UserName is requried',
}

const UserForm = ({
  roles,
  mode,
  openDialog,
  handleCloseDialog,
  data,
  handleSubmit,
  isSubmitting,
}: {
  roles: UserRole[]
  mode: string
  openDialog: boolean
  handleCloseDialog: (e: any) => void
  data: User
  handleSubmit: (e: User) => void
  isSubmitting: boolean
}) => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)

  const formik = useFormik({
    initialValues: data || {
      emailId: '',
      firstName: '',
      gender: '',
      lastName: '',
      middleName: '',
      phoneNumber: '',
      profile: null,
      role: '',
      status: '',
      userId: '',
      userName: '',
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required(validationMessages.requiredName),
      lastName: Yup.string().required(validationMessages.requiredName),
      middleName: Yup.string(),
      // userId: Yup.string().required(validationMessages.requiredEmpId),
      phoneNumber: Yup.string()
        .matches(/^\d{10}$/, validationMessages.invalidPhoneNumber)
        .required(validationMessages.requiredPhoneNumber),
      emailId: Yup.string()
        .email(validationMessages.invalidEmailId)
        .required(validationMessages.requiredEmailId),
      role: Yup.string().required(validationMessages.requiredRole),
      status: Yup.string().required(validationMessages.requiredStatus),
      // profile: Yup.mixed().required(validationMessages.requiredProfileImage),
      gender: Yup.string().required(validationMessages.requriedGender),
      userName: Yup.string().required(validationMessages.requriedUserName),
    }),
    onSubmit: (values) => {
      handleSubmit(values)
    },
  })

  console.log(formik.errors)

  const handleAvatarClick = () => {
    const fileInput = document.createElement('input')
    fileInput.type = 'file'
    fileInput.accept = 'image/*'
    fileInput.onchange = (event: any) => {
      const file: any = event?.target?.files?.[0]
      if (file) {
        formik.setFieldValue('profileImage', URL.createObjectURL(file))
      }
    }
    fileInput.click()
  }

  const handleEmailIdChange = (e: any) => {
    formik.setFieldValue(e?.target?.name, e?.target?.value)
    formik.setFieldValue('userName', e?.target?.value)
  }

  const handleSubmitForm = () => {
    formik.submitForm()
  }

  return (
    <Dialog
      open={openDialog}
      onClose={handleCloseDialog}
      PaperProps={{
        className: classes?.userDialog,
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2 }} id='customized-dialog-title'>
        {mode} Employee
      </DialogTitle>
      <IconButton
        aria-label='close'
        onClick={handleCloseDialog}
        className={classes?.dialogCloseBtn}
      >
        <CloseIcon />
      </IconButton>
      <Divider />
      <DialogContent>
        <form onSubmit={formik.handleSubmit} className={classes?.formContainer}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Avatar
                src={formik?.values?.profile || ''}
                alt='Profile'
                className={classes?.avatar}
                onClick={handleAvatarClick}
              />
              {formik?.touched?.profile && formik?.errors?.profile && (
                <Typography color='error' variant='body2'>
                  {formik?.errors?.profile}
                </Typography>
              )}
            </Grid>
            <Grid container size={{ xs: 12, sm: 6 }} spacing={1}>
              <Typography className={classes?.fieldLabel}>
                First Name
              </Typography>
              <TextField
                fullWidth
                name='firstName'
                value={formik?.values?.firstName}
                onChange={formik?.handleChange}
                onBlur={formik?.handleBlur}
                error={
                  formik?.touched?.firstName &&
                  Boolean(formik?.errors?.firstName)
                }
                helperText={
                  formik?.touched?.firstName && formik?.errors?.firstName
                }
                slotProps={{
                  input: {
                    className: classes?.formTextField,
                  },
                }}
              />
            </Grid>
            <Grid container size={{ xs: 12, sm: 6 }} spacing={1}>
              <Typography className={classes?.fieldLabel}>
                Middle Name
              </Typography>
              <TextField
                fullWidth
                name='middleName'
                value={formik?.values.middleName}
                onChange={formik?.handleChange}
                onBlur={formik?.handleBlur}
                error={
                  formik?.touched?.middleName &&
                  Boolean(formik?.errors?.middleName)
                }
                helperText={
                  formik?.touched?.middleName && formik?.errors?.middleName
                }
                slotProps={{
                  input: {
                    className: classes?.formTextField,
                  },
                }}
              />
            </Grid>
            <Grid container size={{ xs: 12, sm: 6 }} spacing={1}>
              <Typography className={classes?.fieldLabel}>Last Name</Typography>
              <TextField
                fullWidth
                name='lastName'
                value={formik?.values?.lastName}
                onChange={formik?.handleChange}
                onBlur={formik?.handleBlur}
                error={
                  formik?.touched?.lastName && Boolean(formik?.errors?.lastName)
                }
                helperText={
                  formik?.touched?.lastName && formik?.errors?.lastName
                }
                slotProps={{
                  input: {
                    className: classes?.formTextField,
                  },
                }}
              />
            </Grid>

            <Grid container size={{ xs: 12, sm: 6 }} spacing={1}>
              <Typography className={classes?.fieldLabel}>Gender</Typography>
              <TextField
                fullWidth
                select
                name='gender'
                value={formik?.values?.gender}
                onChange={formik?.handleChange}
                onBlur={formik?.handleBlur}
                error={
                  formik?.touched?.gender && Boolean(formik?.errors?.gender)
                }
                helperText={formik?.touched?.gender && formik?.errors?.gender}
                slotProps={{
                  input: {
                    className: classes?.formTextField,
                  },
                }}
              >
                {USER_GENDER?.map((item, index) => (
                  <MenuItem key={index} value={item?.value}>
                    {item?.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {mode === USER_DIALOG_MODES?.UPDATE && (
              <Grid container size={{ xs: 12, sm: 6 }} spacing={1}>
                <Typography className={classes?.fieldLabel}>
                  Employee Id
                </Typography>
                <TextField
                  disabled
                  fullWidth
                  name='userId'
                  value={formik?.values?.userId}
                  onChange={formik?.handleChange}
                  onBlur={formik?.handleBlur}
                  error={
                    formik?.touched?.userId && Boolean(formik?.errors?.userId)
                  }
                  helperText={formik?.touched?.userId && formik?.errors?.userId}
                  slotProps={{
                    input: {
                      className: classes?.formTextField,
                    },
                  }}
                />
              </Grid>
            )}

            <Grid container size={{ xs: 12, sm: 6 }} spacing={1}>
              <Typography className={classes?.fieldLabel}>
                Phone Number
              </Typography>
              <TextField
                fullWidth
                name='phoneNumber'
                value={formik?.values?.phoneNumber}
                onChange={formik?.handleChange}
                onBlur={formik?.handleBlur}
                error={
                  formik?.touched?.phoneNumber &&
                  Boolean(formik?.errors?.phoneNumber)
                }
                helperText={
                  formik?.touched?.phoneNumber && formik?.errors?.phoneNumber
                }
                slotProps={{
                  input: {
                    className: classes?.formTextField,
                  },
                }}
              />
            </Grid>
            <Grid container size={{ xs: 12, sm: 6 }} spacing={1}>
              <Typography className={classes?.fieldLabel}>Email Id</Typography>
              <TextField
                fullWidth
                disabled={mode === USER_DIALOG_MODES?.UPDATE ? true : false}
                name='emailId'
                value={formik?.values?.emailId}
                onChange={(e) => handleEmailIdChange(e)}
                onBlur={formik?.handleBlur}
                error={
                  formik?.touched?.emailId && Boolean(formik?.errors?.emailId)
                }
                helperText={formik?.touched?.emailId && formik?.errors?.emailId}
                slotProps={{
                  input: {
                    className: classes?.formTextField,
                  },
                }}
              />
            </Grid>
            <Grid container size={{ xs: 12, sm: 6 }} spacing={1}>
              <Typography className={classes?.fieldLabel}>Role</Typography>
              <TextField
                fullWidth
                select
                name='role'
                value={formik?.values?.role}
                onChange={formik?.handleChange}
                onBlur={formik?.handleBlur}
                error={formik?.touched?.role && Boolean(formik?.errors?.role)}
                helperText={formik?.touched?.role && formik?.errors?.role}
                slotProps={{
                  input: {
                    className: classes?.formSelectField,
                  },
                }}
              >
                {roles?.map((item, index) => (
                  <MenuItem key={index} value={item?.name}>
                    {item?.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid container size={{ xs: 12, sm: 6 }} spacing={1}>
              <Typography className={classes?.fieldLabel}>Status</Typography>
              <TextField
                fullWidth
                select
                name='status'
                value={formik?.values?.status}
                onChange={formik?.handleChange}
                onBlur={formik?.handleBlur}
                error={
                  formik?.touched?.status && Boolean(formik?.errors?.status)
                }
                helperText={formik?.touched?.status && formik?.errors?.status}
                slotProps={{
                  input: {
                    className: classes?.formSelectField,
                  },
                }}
              >
                {USER_STATUS?.map((item, index) => (
                  <MenuItem key={index} value={item?.value}>
                    {item?.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {mode === USER_DIALOG_MODES?.UPDATE && (
              <Grid container size={{ xs: 12, sm: 6 }} spacing={1}>
                <Typography className={classes?.fieldLabel}>
                  User Name
                </Typography>
                <TextField
                  disabled
                  fullWidth
                  name='userName'
                  value={formik?.values?.userName}
                  onChange={formik?.handleChange}
                  onBlur={formik?.handleBlur}
                  error={
                    formik?.touched?.userName &&
                    Boolean(formik?.errors?.userName)
                  }
                  helperText={
                    formik?.touched?.userName && formik?.errors?.userName
                  }
                  slotProps={{
                    input: {
                      className: classes?.formTextField,
                    },
                  }}
                />
              </Grid>
            )}
          </Grid>
        </form>
      </DialogContent>
      <DialogActions className={classes?.dialogActions}>
        <Button
          variant='contained'
          color='primary'
          className={classes?.submitButton}
          onClick={handleSubmitForm}
          disabled={isSubmitting}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UserForm
